import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon, CheckIcon, DownloadIcon, FileTextIcon, XIcon } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'

import { ApiError } from '@/api/errors'
import { portalApi } from '@/api/portal'
import type { PortalQuoteDetail, QuoteTerms, SignatureInput } from '@/api/types'
import { EmptyState } from '@/components/empty-state'
import { FieldError, FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { Markdown } from '@/components/markdown'
import { PageHeader } from '@/components/page-header'
import {
  PORTAL_PROOFS_KEY,
  SignedGesture,
  SignedProofNotice,
} from '@/components/portal/signed-gesture'
import { QuoteLinesView } from '@/components/quotes/quote-lines-view'
import { QuoteStatusBadge } from '@/components/quotes/quote-status-badge'
import { QuoteTermsBlock } from '@/components/quotes/quote-terms-block'
import { QuoteTotals } from '@/components/quotes/quote-totals'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { downloadDocument } from '@/hooks/use-document-content'
import { msg } from '@/i18n'
import { formatDate, formatDateOnly } from '@/lib/format'
import { formatMoney } from '@/lib/money'

const NONE = 'none'

/**
 * Les refus d'un geste au portail, dans la langue du contact : seuls deux
 * motifs peuvent s'y produire ; le message du serveur reste le repli.
 */
function portalQuoteProblem(error: unknown, fallback: string): string {
  const t = msg().portalForms.quote
  if (!(error instanceof ApiError)) return fallback
  if (error.reason === 'quote_not_open') return t.notOpen
  if (error.reason === 'reason_required') return t.reasonNeeded
  if (error.reason === 'terms_acceptance_required') return t.termsAcceptanceRequired
  return error.message || fallback
}

/**
 * Espace client › une soumission : lignes et totaux **tels que servis**, le
 * PDF (les mêmes octets que l'équipe), et la décision — accepter avec un mot,
 * refuser avec un motif et un mot — quand elle est ouverte (`can_decide`). La
 * décision prise se relit ensuite, telle qu'elle a été donnée.
 */
export const Route = createFileRoute('/portal/soumissions/$quoteId')({
  component: PortalQuotePage,
})

function PortalQuotePage() {
  const t = msg().portalForms.quote
  const { quoteId } = Route.useParams()
  const queryClient = useQueryClient()
  const quote = useQuery({
    queryKey: ['portal', 'quote', quoteId],
    queryFn: () => portalApi.quote(quoteId),
  })
  const [gesture, setGesture] = useState<'accept' | 'decline' | null>(null)

  // Le dialogue se ferme avant le rafraîchissement, qui n'est pas attendu :
  // son bouton ne doit pas redevenir cliquable entre-temps (#301).
  const settle = async (saved: PortalQuoteDetail) => {
    queryClient.setQueryData(['portal', 'quote', quoteId], saved)
    setGesture(null)
    await queryClient.invalidateQueries({ queryKey: ['portal'] })
  }
  const accept = useMutation({
    mutationFn: ({
      message,
      signature,
      termsAccepted,
    }: {
      message: string
      signature: SignatureInput | null
      termsAccepted: boolean
    }) =>
      portalApi.acceptQuote(quoteId, {
        ...(message ? { message } : {}),
        ...(signature ? { signature } : {}),
        // 1.85.0 : l'acceptation des conditions, cochée par le contact lui-même.
        ...(termsAccepted ? { terms_accepted: true } : {}),
      }),
    onSuccess: (saved, { signature }) => {
      if (signature) void queryClient.invalidateQueries({ queryKey: PORTAL_PROOFS_KEY })
      void settle(saved)
      toast.success(t.accepted(saved.number))
    },
  })
  const decline = useMutation({
    mutationFn: ({ reason, message }: { reason: string; message: string }) =>
      portalApi.declineQuote(
        quoteId,
        message ? { reason_code: reason, message } : { reason_code: reason },
      ),
    onSuccess: (saved) => {
      void settle(saved)
      toast.success(t.declined(saved.number))
    },
  })

  if (quote.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    )
  }

  if (quote.isError) {
    return (
      <EmptyState
        icon={FileTextIcon}
        title={t.notFoundTitle}
        description={t.notFound}
        action={
          <Button asChild variant="outline">
            <Link to="/portal/soumissions" search={{}}>
              <ArrowLeftIcon />
              {t.backToQuotes}
            </Link>
          </Button>
        }
      />
    )
  }

  const data = quote.data

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={
          <Button asChild variant="ghost" size="sm">
            <Link to="/portal/soumissions" search={{}}>
              <ArrowLeftIcon />
              {t.back}
            </Link>
          </Button>
        }
        title={
          <span className="flex flex-wrap items-center gap-2">
            <code className="font-key rounded bg-band-foreground/10 px-1.5 py-0.5 text-base font-normal">
              {data.number}
            </code>
            {data.title}
            <QuoteStatusBadge status={data.status} />
          </span>
        }
        documentTitle={`${data.number} — ${data.title}`}
        description={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span>{formatMoney(data.total_cents)}</span>
            {data.valid_until && <span>{t.validUntil(formatDateOnly(data.valid_until))}</span>}
            {data.sent_at && <span>{t.receivedOn(formatDate(data.sent_at))}</span>}
          </span>
        }
        actions={
          data.can_decide ? (
            <span className="flex gap-2">
              <Button
                data-testid="portail-accepter"
                onClick={() => {
                  accept.reset()
                  setGesture('accept')
                }}
              >
                <CheckIcon />
                {t.accept}
              </Button>
              <Button
                variant="outline"
                data-testid="portail-refuser"
                onClick={() => {
                  decline.reset()
                  setGesture('decline')
                }}
              >
                <XIcon />
                {t.decline}
              </Button>
            </span>
          ) : null
        }
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-5">
          {data.intro.trim() !== '' && (
            <Frame>
              <Markdown className="text-sm">{data.intro}</Markdown>
            </Frame>
          )}
          <Frame>
            <h2 className="text-base font-semibold">{t.lines}</h2>
            <QuoteLinesView lines={data.lines} />
          </Frame>
          <Frame>
            <h2 className="text-base font-semibold">{t.totals}</h2>
            <QuoteTotals quote={data} />
          </Frame>
          {data.notes.trim() !== '' && (
            <Frame>
              <h2 className="text-base font-semibold">{t.notes}</h2>
              <Markdown className="text-sm">{data.notes}</Markdown>
            </Frame>
          )}
        </div>

        <div className="space-y-5">
          <DecisionCard quote={data} />
          <Card>
            <CardHeader>
              <CardTitle>{t.document}</CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="w-full"
                data-testid="portail-pdf"
                onClick={() => {
                  void downloadDocument(data.pdf_url, `${data.number}.pdf`).catch(() => {
                    toast.error(t.pdfUnavailable)
                  })
                }}
              >
                <DownloadIcon />
                {t.downloadPdf}
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>{t.terms}</CardTitle>
            </CardHeader>
            <CardContent>
              <QuoteTermsBlock
                paymentTerms={data.payment_terms}
                issuer={data.issuer}
                taxLines={data.tax_lines}
              />
            </CardContent>
          </Card>
          {data.terms && <PortalTermsCard terms={data.terms} />}
        </div>
      </div>

      <AcceptDialog
        open={gesture === 'accept'}
        quote={data}
        pending={accept.isPending}
        error={accept.error}
        onOpenChange={(next) => {
          if (!next) setGesture(null)
        }}
        onValidate={(message, signature, termsAccepted) =>
          accept.mutateAsync({ message, signature, termsAccepted })
        }
      />
      <DeclineDialog
        open={gesture === 'decline'}
        quote={data}
        pending={decline.isPending}
        error={decline.error}
        onOpenChange={(next) => {
          if (!next) setGesture(null)
        }}
        onConfirm={(reason, message) => {
          decline.mutate({ reason, message })
        }}
      />
    </div>
  )
}

function DecisionCard({ quote }: { quote: PortalQuoteDetail }) {
  const t = msg().portalForms.quote
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.decision}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm" data-testid="portail-decision">
        {quote.decision ? (
          <>
            <p className="font-medium">
              {t.decided(
                quote.decision.verdict === 'accepted',
                formatDate(quote.decision.decided_at),
                quote.decision.contact_name,
              )}
            </p>
            {quote.decision.reason_label && <p>{t.reason(quote.decision.reason_label)}</p>}
            {quote.decision.message && (
              <p className="text-muted-foreground">
                {msg().common.format.quote(quote.decision.message)}
              </p>
            )}
            {quote.decision.verdict === 'accepted' && <SignedProofNotice objectId={quote.id} />}
          </>
        ) : quote.can_decide ? (
          <p className="text-muted-foreground">
            {t.waiting(quote.valid_until ? formatDateOnly(quote.valid_until) : null)}
          </p>
        ) : quote.status === 'expired' ? (
          <p className="text-muted-foreground">
            {t.expired(quote.valid_until ? formatDateOnly(quote.valid_until) : null)}
          </p>
        ) : (
          <p className="text-muted-foreground">{t.noDecision}</p>
        )}
      </CardContent>
    </Card>
  )
}

function AcceptDialog({
  open,
  quote,
  pending,
  error,
  onOpenChange,
  onValidate,
}: {
  open: boolean
  quote: PortalQuoteDetail
  pending: boolean
  error: unknown
  onOpenChange: (open: boolean) => void
  /** 1.70.0 : avec la signature, si le client l'exige ou si l'on choisit de signer. */
  onValidate: (
    message: string,
    signature: SignatureInput | null,
    termsAccepted: boolean,
  ) => Promise<unknown>
}) {
  const t = msg().portalForms.quote
  const id = useId()
  const [message, setMessage] = useState('')
  // 1.85.0 : jamais cochée d'avance ; exigée quand la soumission porte des conditions.
  const [agreed, setAgreed] = useState(false)
  const needsTerms = quote.terms !== null
  // Une signature refusée se dit dans son panneau, pas une seconde fois ici.
  const shownError =
    error instanceof ApiError &&
    (error.reason === 'signature_required' || error.reason === 'signature_consent_required')
      ? null
      : error
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setMessage('')
          setAgreed(false)
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t.acceptTitle(quote.number)}</DialogTitle>
          <DialogDescription>
            {t.acceptDescription(quote.title, formatMoney(quote.total_cents))}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={`${id}-message`}>{t.messageLabel}</Label>
          <Textarea
            id={`${id}-message`}
            rows={3}
            value={message}
            disabled={pending}
            onChange={(event) => {
              setMessage(event.target.value)
            }}
          />
        </div>
        {quote.terms && (
          <label className="flex items-start gap-2 text-sm" data-testid="accepter-conditions">
            <Checkbox
              className="mt-0.5"
              checked={agreed}
              disabled={pending}
              onCheckedChange={(checked) => {
                setAgreed(checked === true)
              }}
            />
            <span>{t.termsAccept(quote.terms.version)}</span>
          </label>
        )}
        {shownError ? (
          <FormError>{portalQuoteProblem(shownError, t.acceptFailed)}</FormError>
        ) : null}
        {/* Trois gestes (annuler, accepter, signer) : ils passent à la ligne plutôt que de déborder. */}
        <DialogFooter className="sm:flex-wrap">
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => {
              onOpenChange(false)
            }}
          >
            {t.cancel}
          </Button>
          <SignedGesture
            kind="quote"
            objectId={quote.id}
            callerShowsErrors
            onValidate={(signature) => onValidate(message.trim(), signature, agreed)}
            render={({ validate }) => (
              <Button
                type="button"
                disabled={pending || (needsTerms && !agreed)}
                data-testid="portail-accepter-confirmer"
                onClick={validate}
              >
                {pending ? t.sending : t.acceptConfirm}
              </Button>
            )}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DeclineDialog({
  open,
  quote,
  pending,
  error,
  onOpenChange,
  onConfirm,
}: {
  open: boolean
  quote: PortalQuoteDetail
  pending: boolean
  error: unknown
  onOpenChange: (open: boolean) => void
  onConfirm: (reason: string, message: string) => void
}) {
  const t = msg().portalForms.quote
  const id = useId()
  const [reason, setReason] = useState('')
  const [message, setMessage] = useState('')
  const [touched, setTouched] = useState(false)
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setReason('')
          setMessage('')
          setTouched(false)
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t.declineTitle(quote.number)}</DialogTitle>
          <DialogDescription>{t.declineDescription}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor={`${id}-reason`}>{t.reasonLabel}</Label>
            <Select
              value={reason || NONE}
              disabled={pending}
              onValueChange={(value) => {
                setReason(value === NONE ? '' : value)
              }}
            >
              <SelectTrigger
                id={`${id}-reason`}
                className="w-full"
                aria-invalid={touched && reason === ''}
              >
                <SelectValue placeholder={t.chooseReason} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t.chooseReason}</SelectItem>
                {quote.decline_reasons.map((item) => (
                  <SelectItem key={item.id} value={item.code}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {touched && reason === '' && <FieldError>{t.reasonRequired}</FieldError>}
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${id}-message`}>{t.messageLabel}</Label>
            <Textarea
              id={`${id}-message`}
              rows={3}
              value={message}
              disabled={pending}
              onChange={(event) => {
                setMessage(event.target.value)
              }}
            />
          </div>
        </div>
        {error ? (
          <FormError>
            {error instanceof ApiError && error.reason !== 'quote_not_open'
              ? error.message
              : portalQuoteProblem(error, t.declineFailed)}
          </FormError>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => {
              onOpenChange(false)
            }}
          >
            {t.cancel}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={pending}
            data-testid="portail-refuser-confirmer"
            onClick={() => {
              setTouched(true)
              if (reason === '') return
              onConfirm(reason, message.trim())
            }}
          >
            {pending ? t.sending : t.declineConfirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Les conditions générales jointes à la soumission (1.85.0), figées à l'envoi.
 * En anglais, la version française reste à portée d'un clic (Charte de la
 * langue française : un contrat d'adhésion est d'abord remis en français).
 */
function PortalTermsCard({ terms }: { terms: QuoteTerms }) {
  const t = msg().portalForms.quote
  const [french, setFrench] = useState(false)
  const sections = french ? terms.sections_fr : terms.sections
  return (
    <Card data-testid="portail-conditions">
      <CardHeader>
        <CardTitle>{t.termsTitle}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">{t.termsVersion(terms.version)}</p>
        {terms.language === 'en' && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setFrench((current) => !current)
            }}
          >
            {french ? t.termsOriginal : t.termsFrench}
          </Button>
        )}
        <div
          className="max-h-96 space-y-4 overflow-y-auto rounded-md border p-3"
          lang={french ? 'fr' : terms.language}
          data-testid="portail-texte-conditions"
        >
          {sections.map((section) => (
            <article key={section.part} className="space-y-1">
              <h3 className="font-semibold">{section.title}</h3>
              <Markdown className="text-sm">{section.body}</Markdown>
            </article>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
